import React, { useState, useEffect } from 'react';
import { MsFormulaire, OperationMS } from '../types';
import { CRITERES_TECHNIQUES, CRITERES_MEMO } from '../domain/analyse/notationTechnique';
import { getLancementConsultationInfo } from '../domain/analyse/historique';
import AccueilModal from '../../../composants/AccueilModal/AccueilModal';

interface OperationProceduralRecapProps {
  operation: OperationMS;
  formData: MsFormulaire;
  viewMode: boolean;
  errors?: Record<string, string>;
  setFormData?: (data: MsFormulaire) => void;
}

// Champ LECTURE SEULE (libelle + valeur, "-" si absent), span de colonnes parametrable.
const ChampRO: React.FC<{ label: string; value?: string; col?: number; className?: string }> = ({
  label,
  value,
  col = 3,
  className,
}) => (
  <div className={`form-group${className ? ` ${className}` : ''}`} style={{ gridColumn: `span ${col}` }}>
    <label title={label}>{label}</label>
    <div className="input-inactif">{value && value.trim() ? value : '-'}</div>
  </div>
);

// Affiche une date-heure FR en lecture seule ("JJ/MM/AAAA HH:mm (GMT Paris)").
const formatDateTimeFr = (v?: string): string => {
  if (!v) return '-';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return v;
  return `${d.toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })} (GMT Paris)`;
};

// Convertit une date en chaine "YYYY-MM-DDTHH:mm" (heure locale) pour <input type="datetime-local">.
const toDateTimeLocal = (d: Date): string => {
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

// Champ date-heure SAISI par l'acheteur (datetime-local), span 3, avec bascule lecture seule.
const ChampDateTime: React.FC<{
  label: string;
  value?: string;
  viewMode: boolean;
  required?: boolean;
  max?: string;
  min?: string;
  error?: string;
  onChange: (v: string) => void;
}> = ({ label, value, viewMode, required, max, min, error, onChange }) => (
  <div className="form-group" style={{ gridColumn: 'span 3' }}>
    <label>
      {label} {!viewMode && required && <span style={{ color: 'red' }}>*</span>}
    </label>
    {viewMode ? (
      <div className="input-inactif">{formatDateTimeFr(value)}</div>
    ) : (
      <>
        {/* "(GMT Paris)" DANS le champ : le wrapper porte la bordure, l'input est sans bordure a l'interieur. */}
        <div className={`opms__datetime-gmt${error ? ' error' : ''}`}>
          <input type="datetime-local" value={value || ''} max={max} min={min} onChange={(e) => onChange(e.target.value)} />
          <span className="opms__datetime-gmt-suffix">(GMT Paris)</span>
        </div>
        {error && <span className="error-message">{error}</span>}
      </>
    )}
  </div>
);

// Haut du "Descriptif procedural" (refonte xlsx, lignes 57-64). FRONT SEUL : les champs derives
// (accord-cadre, validite, dates estimees, rappel des criteres) sont en lecture seule, alimentes par
// l'operation quand la donnee existe (lots), sinon "-" en attendant le backend. Les champs SAISIS par
// l'acheteur (duree de consultation, budget, origine du financement, module Q/R) sont editables.
const OperationProceduralRecap: React.FC<OperationProceduralRecapProps> = ({
  operation,
  formData,
  viewMode,
  errors = {},
  setFormData,
}) => {
  const update = (patch: Partial<MsFormulaire>) => {
    if (setFormData) setFormData({ ...formData, ...patch });
  };

  // Rappel du critere technique : valeurs REELLES (sous-criteres du bareme en vigueur) + modale "tout".
  const [showCriteres, setShowCriteres] = useState(false);
  const critereTechniqueResume = CRITERES_TECHNIQUES
    .map((c) => `Sur ${c.maxPoints} point${c.maxPoints > 1 ? 's' : ''} : ${c.label}`)
    .join(' · ');
  // Apercu tronque cote DONNEE (pas de -webkit-line-clamp CSS) : borne le texte pour qu'il tienne en
  // ~2 lignes (champ span-3) en laissant la place au bouton "lire la suite" inline. Complet en modale.
  const APERCU_MAX = 65;
  const critereTechniqueApercu =
    critereTechniqueResume.length > APERCU_MAX
      ? `${critereTechniqueResume.slice(0, APERCU_MAX).trimEnd()}…`
      : critereTechniqueResume;

  const lotTechnique = [operation.lot_technique?.id, operation.lot_technique?.nom].filter(Boolean).join(' - ');
  const lotGeographique = [operation.lot_departement?.id_departement, operation.lot_departement?.departement?.nom]
    .filter(Boolean)
    .join(' - ');

  // Date limite de reception des offres : DEDUCTIBLE de la duree, pre-remplie mais MODIFIABLE.
  // Defaut (format datetime-local) = expire_at reel si lance, sinon (debut de consultation ou
  // maintenant) + duree, heure conservee. L'acheteur peut l'ecraser via dateLimiteReceptionOffres.
  const dateLimiteDefault = (() => {
    const base = operation.expire_at
      ? new Date(operation.expire_at)
      : (() => {
          const b = formData.dateDebutConsultation ? new Date(formData.dateDebutConsultation) : new Date();
          const f = new Date(b);
          f.setDate(b.getDate() + (Number(formData.delaisConsultation) || 10));
          return f;
        })();
    return Number.isNaN(base.getTime()) ? '' : toDateTimeLocal(base);
  })();

  // Borne mini = maintenant : la date/heure limite de reception ne peut PAS etre dans le passe.
  // Aucune borne max : l'acheteur choisit librement la date et l'heure (seule regle = pas de passe).
  const minDateTime = toDateTimeLocal(new Date());

  // La date limite affiche un defaut deduit mais le laissait VIDE dans le formulaire (sauvegarde '').
  // On commit ce defaut des qu'il est vide : le champ obligatoire est reellement rempli (pas de faux
  // positif de validation) et la valeur est persistee. Mirroir du commit de dateDebutOperation.
  useEffect(() => {
    if (viewMode || !setFormData) return;
    if (!formData.dateLimiteReceptionOffres && dateLimiteDefault) {
      update({ dateLimiteReceptionOffres: dateLimiteDefault });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, dateLimiteDefault, formData.dateLimiteReceptionOffres]);

  // Duree de consultation (jours calendaires) = intervalle entre le LANCEMENT (ou aujourd'hui si pas
  // encore lance) et la DATE LIMITE de reception des offres. DEDUITE (read-only).
  const dureeConsultationJours = (() => {
    const limiteStr = formData.dateLimiteReceptionOffres || dateLimiteDefault;
    if (!limiteStr) return null;
    const fin = new Date(limiteStr);
    if (Number.isNaN(fin.getTime())) return null;
    const lancementStr = getLancementConsultationInfo(operation.historique).date;
    const debut = lancementStr ? new Date(lancementStr) : new Date();
    if (Number.isNaN(debut.getTime())) return null;
    return Math.max(0, Math.ceil((fin.getTime() - debut.getTime()) / (1000 * 60 * 60 * 24)));
  })();

  return (
    <div className="opms__descriptif-admin">
      <div className="opms__admin-grid">
        {/* Ligne 57/58 : accord-cadre + lots (derives, lecture seule).
            TODO backend : Accord-cadre en vigueur + Date de validite sont EN DUR pour l'instant ; a
            remplacer par les vraies valeurs de l'accord-cadre rattache a l'operation (doivent suivre). */}
        <ChampRO label="Accord-cadre en vigueur" value="Accord-cadre édition 2025-2026" />
        <ChampRO label="Date de validité" value="01/01/2025 au 31/12/2026" />
        <ChampRO label="Lot technique" value={lotTechnique} />
        <ChampRO label="Lot géographique" value={lotGeographique} />

        {/* Ligne 59/60 : date limite = pre-remplie (deduite) mais MODIFIABLE, seule borne = pas dans le
            passe (min). Aucune borne haute : la date de demarrage est declarative. Duree = read-only ;
            ouverture/notification = saisies. */}
        <ChampDateTime
          label="Date et heure limite de réception des offres"
          value={formData.dateLimiteReceptionOffres || dateLimiteDefault}
          viewMode={viewMode}
          required
          min={minDateTime}
          error={errors.dateLimiteReceptionOffres}
          onChange={(v) => update({ dateLimiteReceptionOffres: v && v < minDateTime ? minDateTime : v })}
        />
        <ChampRO
          label="Durée de consultation (jours calendaires)"
          value={dureeConsultationJours !== null ? `${dureeConsultationJours} jours` : ''}
        />
        <ChampDateTime
          label="Ouverture des plis prévue le (estimation)"
          value={formData.dateOuverturePlis}
          viewMode={viewMode}
          required
          error={errors.dateOuverturePlis}
          onChange={(v) => update({ dateOuverturePlis: v })}
        />
        <ChampDateTime
          label="Notification prévue le (estimation)"
          value={formData.dateNotification}
          viewMode={viewMode}
          required
          error={errors.dateNotification}
          onChange={(v) => update({ dateNotification: v })}
        />

        {/* Ligne 61/62 : budget + financement (SAISIS, facultatifs) ; sous-traitance (derivee) ;
            module Q/R (SAISI, select). */}
        <div className="form-group" style={{ gridColumn: 'span 3' }}>
          <label>Budget maximum du marché (facultatif)</label>
          {viewMode ? (
            <div className="input-inactif">{formData.budgetMaximum || '-'}</div>
          ) : (
            <input
              type="text"
              value={formData.budgetMaximum || ''}
              onChange={(e) => update({ budgetMaximum: e.target.value })}
              placeholder="ex : 450 000€"
            />
          )}
        </div>
        <div className="form-group" style={{ gridColumn: 'span 3' }}>
          <label>Origine du financement (facultatif)</label>
          {viewMode ? (
            <div className="input-inactif">{formData.origineFinancement || '-'}</div>
          ) : (
            <input
              type="text"
              value={formData.origineFinancement || ''}
              onChange={(e) => update({ origineFinancement: e.target.value })}
              placeholder="ex : 60% Europe, 40% Commune"
            />
          )}
        </div>
        <ChampRO label="Sous-traitance autorisée" value="DC4 à présenter après notification" />
        <ChampRO label="Module Questions/Réponses" value="Activé (disponible dès le lancement)" />

        {/* Ligne 63/64 : rappel des criteres et ponderations (derives de l'accord-cadre, lecture seule).
            TODO backend : EN DUR pour l'instant ; a remplacer par les vrais criteres/ponderations de
            l'accord-cadre rattache a l'operation (doivent suivre). */}
        <div className="form-group opms__rappel-champ opms__rappel-champ--technique" style={{ gridColumn: 'span 3' }}>
          <label>Rappel du Critère Technique et des sous-critères</label>
          <div className="input-inactif">
            {critereTechniqueApercu}{' '}
            <button type="button" className="opms__lire-la-suite" onClick={() => setShowCriteres(true)}>
              lire la suite
            </button>
          </div>
        </div>
        <ChampRO className="opms__rappel-champ" label="Rappel de la note et pondération" value="Sur 10 points pondérés à 55%" />
        <ChampRO
          className="opms__rappel-champ"
          label="Rappel du critère Financier"
          value="(prix le plus bas/prix de l'offre examinée) x 10. Les prix sont analysés en HT."
        />
        <ChampRO className="opms__rappel-champ" label="Rappel de la note et pondération" value="Sur 10 points pondérés à 45%" />
      </div>

      <AccueilModal
        isOpen={showCriteres}
        onClose={() => setShowCriteres(false)}
        title="Rappel du Critère Technique et des sous-critères"
        className="opms__critere-modal"
      >
        <div>
          {CRITERES_MEMO.split('\n').map((ligne, i) => (
            <p key={i} style={{ margin: '0 0 1.6rem', lineHeight: 1.6 }}>
              {ligne}
            </p>
          ))}
        </div>
      </AccueilModal>
    </div>
  );
};

export default OperationProceduralRecap;
